import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import ListItemIcon from "@mui/material/ListItemIcon";
import SettingsIcon from "@mui/icons-material/Settings";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TimelineIcon from "@mui/icons-material/Timeline";
import PlaceIcon from "@mui/icons-material/Place";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BoltIcon from "@mui/icons-material/Bolt";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useNavigate, useLocation } from "react-router-dom";
import LSPSStatus from "../../Features/OperatorDashboard/LSPSStatus";
import ProfileDropDown from "./ProfileDropDown";
import config from "../../config";
const DRAWER_WIDTH = 272;

function SectionLabel({ children }) {
  return (
    <Typography
      sx={{
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,0.3)",
        px: "20px",
        pt: "20px",
        pb: "6px",
      }}
    >
      {children}
    </Typography>
  );
}

function NavItem({ label, icon, onClick, active = false, dimmed = false }) {
  return (
    <ListItem
      button
      onClick={onClick}
      sx={{
        borderRadius: "8px",
        mx: "10px",
        width: `calc(100% - 20px)`,
        px: "12px",
        py: "9px",
        mb: "2px",
        bgcolor: active ? "rgba(253,185,64,0.12)" : "transparent",
        "&:hover": {
          bgcolor: active ? "rgba(253,185,64,0.18)" : "rgba(255,255,255,0.05)",
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 32,
          color: active
            ? "#FDB940"
            : dimmed
            ? "rgba(255,255,255,0.28)"
            : "rgba(255,255,255,0.55)",
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: {
            fontSize: "0.9rem",
            fontWeight: active ? 600 : 500,
            color: active
              ? "#FDB940"
              : dimmed
              ? "rgba(255,255,255,0.3)"
              : "#e0e6f0",
          },
        }}
      />
    </ListItem>
  );
}

export default function OperatorNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const close = () => setIsDrawerOpen(false);
  const go = (path) => { navigate(path); close(); };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: "#161B22",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          zIndex: 1400,
        }}
      >
        <Toolbar sx={{ gap: "4px" }}>
          {/* Hamburger */}
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setIsDrawerOpen((v) => !v)}
            sx={{ mr: "4px" }}
          >
            {isDrawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
          </IconButton>

          {/* Tjommie logo */}
          <Box
            sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <img src="/assets/tjommie-logo.svg" height="24" alt="Tjommie" style={{ display: "block" }} />
          </Box>

          {/* Vertical separator */}
          <Box sx={{ width: "1px", height: "18px", backgroundColor: "rgba(255,255,255,0.15)", mx: "12px", flexShrink: 0 }} />

          {/* App label */}
          <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.88rem", fontWeight: 400, flexGrow: 1 }}>
            DSM Operator
          </Typography>

          {/* Peak Shaving / Load Shifting: status only. Switched in Settings, behind a
              type-CONFIRM dialog (NEU-1226). */}
          <LSPSStatus />

          {/* Profile avatar */}
          <ProfileDropDown />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={isDrawerOpen}
        onClose={close}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            backgroundColor: "#0d1117",
            borderRight: "1px solid rgba(255,255,255,0.07)",
          },
        }}
      >
        {/* ── Drawer header ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: "16px",
            py: "16px",
            borderBottom: "1px solid rgba(255,255,255,0.07)",
          }}
        >
          {/* Tjommie logo */}
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <img src="/assets/tjommie-logo.svg" height="28" alt="Tjommie" style={{ display: "block" }} />
          </Box>

          {/* Close button */}
          <IconButton
            size="small"
            onClick={close}
            sx={{
              color: "rgba(255,255,255,0.45)",
              border: "1px solid rgba(255,255,255,0.12)",
              borderRadius: "6px",
              p: "4px",
              "&:hover": { bgcolor: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.8)" },
            }}
          >
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        {/* ── Main nav ── */}
        <List sx={{ px: 0, pt: "12px", pb: 0 }}>
          <NavItem label="Home"     icon={<WbSunnyIcon sx={{ fontSize: "1.1rem" }} />}     onClick={() => go("/")}                      active={isActive("/")} />
          <NavItem label="Settings" icon={<SettingsIcon sx={{ fontSize: "1.1rem" }} />}     onClick={() => go("/operator/settings/")}    active={isActive("/operator/settings/")} />
          <NavItem label="ADD"     icon={<StarBorderIcon sx={{ fontSize: "1.1rem" }} />}   onClick={() => go("/operator/admd/")}        active={isActive("/operator/admd/")} />
        </List>

        {/* ── Engineering section ── */}
        <SectionLabel>Engineering</SectionLabel>
        <List sx={{ px: 0, pt: 0, pb: 0 }}>
          <NavItem label="Historical Graph" icon={<TimelineIcon    sx={{ fontSize: "1.1rem" }} />} onClick={() => go("/operator/historical-graph/")}  active={isActive("/operator/historical-graph/")} />
          <NavItem label="Map Overview"     icon={<PlaceIcon       sx={{ fontSize: "1.1rem" }} />} onClick={() => go("/operator/map/")}                active={isActive("/operator/map/")} />
          <NavItem label="Notch Export"     icon={<FileDownloadIcon sx={{ fontSize: "1.1rem" }} />} onClick={() => go("/operator/notch-export/")}      active={isActive("/operator/notch-export/")} />
        </List>

        {/* ── Control section ── */}
        <SectionLabel>Control</SectionLabel>
        <List sx={{ px: 0, pt: 0, pb: 0 }}>
          <NavItem label="Switching" icon={<PowerSettingsNewIcon sx={{ fontSize: "1.1rem" }} />} onClick={() => go("/operator/switching/")} active={isActive("/operator/switching/")} />
        </List>

        {/* ── Other roles section ── */}
        <SectionLabel>Other Roles</SectionLabel>
        <List sx={{ px: 0, pt: 0, pb: 2 }}>
          <NavItem label="Municipality" icon={<BoltIcon sx={{ fontSize: "1.1rem" }} />}          onClick={() => go("/municipality/")} />
          <NavItem label="Utility"      icon={<BoltIcon sx={{ fontSize: "1.1rem" }} />}          onClick={() => go("/utility/")} />
          <NavItem label="Installer"    icon={<LocalShippingIcon sx={{ fontSize: "1.1rem" }} />} onClick={() => { close(); window.location.href = config.REACT_APP_INSTALLER_URL; }} />
        </List>
      </Drawer>
    </>
  );
}
